import { IComment } from '@sdk/contracts';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CommentAnswer } from '../CommentAnswer';
import { CommentContent } from '../CommentContent';
import { UserInfo } from '../UserInfo';

import {
  AvatarThreadWrapper,
  ItemWrapper,
  SeeMoreAnswersButton,
  SeeMoreAnswersWrapper,
  ThreadWrapper,
  Wrapper
} from './CommentItem.styles';

import { Icon } from '@/components/Icon';
import { useMutateEvents } from '@/store';

interface ICommentItemProps {
  comment: IComment;
  isHighlighted: boolean;
  id: string;
  eventId?: string;
}

export const CommentItem: React.FC<ICommentItemProps> = ({
  comment,
  isHighlighted,
  id,
  eventId
}) => {
  const { t } = useTranslation('StudentCourseTranslations');
  const [displayAnswersIndex, setDisplayAnswersIndex] = useState(1);
  const { removeComment } = useMutateEvents();
  const filteredMessages = useMemo(
    () =>
      comment?.messages?.sort(
        (a, b) => (b.usefulAnswer?.length ?? 0) - (a.usefulAnswer?.length ?? 0)
      ),
    [comment?.messages]
  );

  const messages = useMemo(() => {
    return filteredMessages?.slice(0, displayAnswersIndex);
  }, [filteredMessages, displayAnswersIndex]);

  const loadMoreAnswers = () => setDisplayAnswersIndex(displayAnswersIndex + 3);
  const collapseAnswers = () => setDisplayAnswersIndex(1);

  const isLastComment = useMemo(
    () => messages?.length === filteredMessages?.length,
    [messages, filteredMessages]
  );

  const handleDeleteMessage = () => {
    if (!eventId) return;
    removeComment({ commentId: comment._id, eventId });
  };
  return (
    <ItemWrapper id={id}>
      <Wrapper>
        <UserInfo comment={comment} />
        <CommentContent
          canAnswer
          content={comment.content}
          sender={comment.sender}
          file={comment.attached}
          createdAt={comment.createdAt}
          commentId={comment._id}
          onDeleteMessage={handleDeleteMessage}
          isHighlighted={isHighlighted}
        />
      </Wrapper>
      {messages?.map((message, cmtIndex) => (
        <CommentAnswer
          key={message._id}
          message={message}
          commentId={comment._id}
          isLastMessage={cmtIndex === messages?.length - 1 && (filteredMessages?.length ?? 0) <= 1}
        />
      ))}
      {filteredMessages && (filteredMessages?.length ?? 0) > 1 && (
        <SeeMoreAnswersWrapper>
          <ThreadWrapper>
            <AvatarThreadWrapper />
          </ThreadWrapper>
          <SeeMoreAnswersButton onClick={isLastComment ? collapseAnswers : loadMoreAnswers}>
            <Icon i={isLastComment ? 'ArrowUp' : 'ArrowDown'} />
            {isLastComment ? t('SEE_LESS_ANSWERS') : t('SEE_MORE_ANSWERS')}
          </SeeMoreAnswersButton>
        </SeeMoreAnswersWrapper>
      )}
    </ItemWrapper>
  );
};
